import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { useTheme } from '../../context/ThemeContext';
import { noticeService, classService, subjectService } from '../../services/api';

const TeacherNotices: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [notices, setNotices] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'general',
    priority: 'medium',
    targetAudience: [] as string[],
    classes: [] as string[],
    subjects: [] as string[]
  });

  useEffect(() => {
    fetchNotices();
  }, []);

  const fetchNotices = async () => {
    try {
      setLoading(true);
      const [noticesRes, classesRes, subjectsRes] = await Promise.all([
        noticeService.getAll(),
        classService.getAll(),
        subjectService.getAll()
      ]);
      
      const noticesData = Array.isArray(noticesRes.data) 
        ? noticesRes.data 
        : (noticesRes.data?.notices || []);
      const classesData = Array.isArray(classesRes.data) 
        ? classesRes.data 
        : (classesRes.data?.classes || []);
      const subjectsData = Array.isArray(subjectsRes.data) 
        ? subjectsRes.data 
        : (subjectsRes.data?.subjects || []);
        
      setNotices(noticesData);
      setClasses(classesData);
      setSubjects(subjectsData);
    } catch (error) {
      console.error('Failed to fetch notices:', error);
      alert('Failed to load notices');
      setNotices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // If no targetAudience selected, default to 'all' to show to everyone
      const dataToSubmit = {
        ...formData,
        targetAudience: formData.targetAudience.length > 0 
          ? formData.targetAudience 
          : ['all']
      };
      
      await noticeService.create(dataToSubmit);
      alert('Notice posted successfully!');
      setShowForm(false);
      setFormData({ 
        title: '', 
        content: '', 
        type: 'general', 
        priority: 'medium',
        targetAudience: [],
        classes: [],
        subjects: []
      });
      fetchNotices();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to post notice');
    }
  };

  return (
    <Layout title="Notices">
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
        <div className="mb-6 flex justify-between items-center">
          <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Notices
          </h2>
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            {showForm ? 'Cancel' : '+ Post Notice'}
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="mb-6 p-4 border rounded">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className={`w-full px-4 py-2 rounded border ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Content *
                </label>
                <textarea
                  required
                  rows={6}
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  className={`w-full px-4 py-2 rounded border ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Type
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    className={`w-full px-4 py-2 rounded border ${
                      isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="general">General</option>
                    <option value="exam">Exam</option>
                    <option value="event">Event</option>
                    <option value="holiday">Holiday</option>
                    <option value="urgent">Urgent</option>
                    <option value="academic">Academic</option>
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Priority
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                    className={`w-full px-4 py-2 rounded border ${
                      isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              {/* Target Audience Selection */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Target Audience
                </label>
                <div className="flex gap-4">
                  {['student', 'teacher', 'parent'].map(role => (
                    <label key={role} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.targetAudience.includes(role)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({...formData, targetAudience: [...formData.targetAudience, role]});
                          } else {
                            setFormData({...formData, targetAudience: formData.targetAudience.filter(r => r !== role)});
                          }
                        }}
                        className="mr-2"
                      />
                      <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                        {role.charAt(0).toUpperCase() + role.slice(1)}s
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Classes Selection */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Specific Classes (Optional)
                </label>
                <select
                  multiple
                  value={formData.classes}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    setFormData({...formData, classes: selected});
                  }}
                  className={`w-full px-4 py-2 rounded border ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  size={5}
                >
                  {classes.map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name} - {cls.section}
                    </option>
                  ))}
                </select>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Hold Ctrl/Cmd to select multiple classes. Leave empty for all classes.
                </p>
              </div>

              {/* Subjects Selection */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Specific Subjects (Optional)
                </label>
                <select
                  multiple
                  value={formData.subjects}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    setFormData({...formData, subjects: selected});
                  }}
                  className={`w-full px-4 py-2 rounded border ${
                    isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                  size={5}
                >
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name} ({subject.code})
                    </option>
                  ))}
                </select>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Hold Ctrl/Cmd to select multiple subjects. Leave empty for all subjects.
                </p>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                type="submit"
                className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700"
              >
                Post Notice
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : (
          <div className="space-y-4">
            {notices.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No notices found
              </div>
            ) : (
              notices.map((notice) => (
                <div
                  key={notice.id}
                  className={`border rounded-lg p-4 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {notice.title}
                      </h3>
                      <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {notice.content}
                      </p>
                      <div className="mt-2 flex gap-2">
                        <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800">
                          {notice.type}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          notice.priority === 'high' ? 'bg-red-100 text-red-800' :
                          notice.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {notice.priority}
                        </span>
                      </div>
                    </div>
                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {new Date(notice.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TeacherNotices;
